import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { generateColumn } from 'src/common/components/table/LegacyTable';
import { useCurrentOrganization } from 'src/store/organization';
import { DesktopStrongIcon, MobileStrongIcon, TabletStrongIcon, UnknownStrongIcon } from 'src/monet/icons';
import { formatDuration } from 'src/utils/date';
import { DataTable } from 'src/common/components/table/data-table';
import { TooltipComponent } from 'src/common/tooltip/component';
import { useHistory } from 'react-router-dom';
import { VisitorView } from 'src/editor/insights/constants';
import { getDeviceTypeTooltip } from 'src/editor/insights/visitor-card';
import { ImageRenderer } from 'src/common/ImageRenderer';
import { Row } from 'react-table';
import { EllipsizedText } from 'src/common/components/table/ellipsized-text';
import { TimePeriodParam } from 'src/dashboard/insights/constants';
import { TableHeaderWithTooltip } from 'src/dashboard/insights/table-header-with-tooltip';
import { DashboardInsightsJourneysJourneyUtmDetailsSlideover } from '../journeys/journey/utm-details-slideover';
import { useCurrentSubscription } from 'src/store/subscription';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';
import { useJourneys } from 'src/store/journeys';

import classNames from 'classnames';

type Props = {
  timePeriodParam?: TimePeriodParam;
  insights: any;
  setAllVisitorsFetcher: (fetcher: () => Promise<{ views: VisitorView[] }>) => void;
};

export const DashboardInsightsJourneysJourneyVisitorsTable = ({
  timePeriodParam,
  insights,
  setAllVisitorsFetcher,
}: Props) => {
  const history = useHistory();
  const [utmPanelOpen, setUtmPanelOpen] = useState(false);
  const [utmParams, setUtmParams] = useState<string>();

  const { currentOrganization } = useCurrentOrganization();
  const { subscriptionPlanType, isOnTrial } = useCurrentSubscription();
  const { features, showUpgradeModal } = useFeatureRestrictions();
  const journeys = useJourneys((state) => state.journeys);

  const SLIDE_OVER_ANIMATION_DURATION_IN_MS = 200;

  const columns = useMemo(
    () => [
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Journey Name'>Journey name</TableHeaderWithTooltip>,
        accessor: 'journey-name',
        width: 200,
        Cell: ({ row, index }: { row: any; index: number }) => {
          return <EllipsizedText text={row.original.journey_name || 'Untitled'} />
        },
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Creator'>Journey Creator</TableHeaderWithTooltip>,
        accessor: 'journey-creator',
        allowShrinking: true,
        width: '10%',
        Cell: ({ row, index }: { row: any; index: number }) => {
          return <EllipsizedText text={row.original.owner_name || row.original.owner_email || ''} />
        },
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Visitor name or email'>Visitor name</TableHeaderWithTooltip>,
        accessor: 'name',
        width: 156,
        Cell: ({ row, index }: { row: any; index: number }) => (
          <EllipsizedText text={row.original.name || 'Anonymous'} />
        ),
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='When they viewed'>Viewed</TableHeaderWithTooltip>,
        accessor: 'viewed',
        width: 100,
        Cell: ({ row, index }: { row: any; index: number }) => row.original.created_at,
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Time spent viewing Journey'>Time spent</TableHeaderWithTooltip>,
        accessor: 'time',
        width: 90,
        Cell: ({ row, index }: { row: any; index: number }) => {
          const visitorView = row.original as VisitorView;
          const { duration } = visitorView;
          return duration ? formatDuration(duration) : '-';
        },
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='How many steps were viewed'>Completion</TableHeaderWithTooltip>,
        accessor: 'completion',
        width: 100,
        Cell: ({ row, index }: { row: any; index: number }) => `${Math.round(row.original.completed_percentage)}%`,
      }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Visitor email if captured'>Email address</TableHeaderWithTooltip>,
        accessor: 'email_address',
        width: 176,
        Cell: ({ row, index }: { row: any; index: number }) => <EllipsizedText text={row.original.email || ''} />,
      }),
    //   generateColumn({
    //     Header: <TableHeaderWithTooltip tooltip='Website domain'>Website</TableHeaderWithTooltip>,
    //     accessor: 'website',
    //     width: '100%',
    //     Cell: ({ row }: { row: any }) => <EllipsizedText text={row.original.website || ''} />,
    //   }),
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Inferred company name'>Company</TableHeaderWithTooltip>,
        accessor: 'company',
        width: 138,
        Cell: ({ row, index }: { row: any; index: number }) => {
          const visitorView = row.original as VisitorView;
          const { company } = visitorView;
          if (company) {
            return (
              <div className='flex items-start gap-2'>
                {company.logo && <ImageRenderer className='rounded-md w-5 h-5' src={company.logo} />}
                {company.name && <div>{company.name}</div>}
              </div>
            );
          }
          return null;
        },
      }),

      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Link name that was opened'>Link</TableHeaderWithTooltip>,
        accessor: 'link',
        width: 98,
        Cell: ({ row, index }: { row: any; index: number }) => {
          const visitorView = row.original as VisitorView;
          const { alias_id, utm_params, alias_name } = visitorView;
          if (alias_id) {
            return (
              <div
               
              >
                <EllipsizedText text={alias_name || ''} />
              </div>
            );
          }
          return null;
        },
      }),
      
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='UTM Parameters'>UTM</TableHeaderWithTooltip>,
        accessor: 'utm',
        width: 120,
        Cell: ({ row, index }: { row: any; index: number }) => {
          const visitorView = row.original as VisitorView;
          const { utm_parameters } = visitorView;
          
          if (utm_parameters && Object.keys(utm_parameters).length > 0) {
            return (
              <div 
                className="underline cursor-pointer"
                onClick={(e: any) => {
                  e.stopPropagation();
                  // Format utm_parameters object into string format
                  const utmString = Object.entries(utm_parameters)
                    .map(([key, value]) => `${key}=${value}`)
                    .join('&');
                  setUtmParams(utmString);
                  setUtmPanelOpen(true);
                }}
              >
                View Details
              </div>
            );
          }
          
          return null;
        },
      }),

      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Location of viewer'>Location</TableHeaderWithTooltip>,
        accessor: 'location',
        width: '100%',
        Cell: ({ row, index }: { row: any; index: number }) => row.original.location || '',
      }),
    //   generateColumn({
    //     Header: <TableHeaderWithTooltip tooltip='IP address of viewer'>IP address</TableHeaderWithTooltip>,
    //     accessor: 'ip_address',
    //     width: 100,
    //     Cell: ({ row, index }: { row: any; index: number }) => row.original.ip_address || '',
    //   }),
    
      generateColumn({
        Header: <TableHeaderWithTooltip tooltip='Device type'>Device</TableHeaderWithTooltip>,
        accessor: 'device',
        width: '100%',
        Cell: ({ row, index }: { row: any; index: number }) => {
          const visitorView = row.original as VisitorView;
          const { device_type } = visitorView;
          return (
            <div className='flex'>
              <TooltipComponent
                placement='top'
                tooltipContent={getDeviceTypeTooltip(device_type)}
                styling='tooltip'
                theme='journey'
              >
                {device_type?.form === 'mobile' ? (
                  <MobileStrongIcon />
                ) : device_type?.form === 'desktop' ? (
                  <DesktopStrongIcon />
                ) : device_type?.form === 'tablet' ? (
                  <TabletStrongIcon />
                ) : (
                  <UnknownStrongIcon />
                )}
              </TooltipComponent>
            </div>
          );
        },
      }),
    ],
    []
  );

  const onRowClicked = (row: Row) => {
    const { session_uuid, journey_uuid } = row.original as any;
    
    // Check if journey is accessible
    const journey = journeys.find((j: any) => j.uuid === journey_uuid);
    if (journey && !features.canAccessBasicInsights) {
      showUpgradeModal(
        'basic_insights',
        'You need access to basic insights to view visitor analytics. Please contact support.',
        'Insights Access Required'
      );
      return;
    }
    
    history.push(`/${currentOrganization.slug}/insights/journeys/${journey_uuid}/${session_uuid.replace(/-/g, '')}`);
  };

  return (
    <div className='mb-6'>
      <DataTable tableClassName='w-full' columns={columns} data={insights} onRowClick={onRowClicked} />
      <DashboardInsightsJourneysJourneyUtmDetailsSlideover
        open={utmPanelOpen}
        onClose={() => {
          setUtmPanelOpen(false);
          setTimeout(() => {
            setUtmParams(undefined);
          }, SLIDE_OVER_ANIMATION_DURATION_IN_MS);
        }}
        utmParams={utmParams}
      />
    </div>
  );
};
